import TextFieldWrapper, { TextFieldWrapperProps } from 'ComponentLibrary/src/atoms/forms/FormTextFields/TextFieldWrapper/TextFieldWrapper'
import React from 'react'
import { Control, Controller, ControllerRenderProps, FieldErrors, FieldValues, Path, PathValue, RegisterOptions } from 'react-hook-form'
import { get } from 'lodash'
import { Box, FormHelperText, styled } from '@mui/material'
import { ToolbarPlugin, type ToolbarPluginCopy } from 'ComponentLibrary/src/molecules/TextEditor/ToolbarPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ListItemNode, ListNode } from '@lexical/list'
import { $convertFromMarkdownString, $convertToMarkdownString, TRANSFORMERS } from '@lexical/markdown'
import { type EditorState } from 'lexical'
import { HeadingNode } from '@lexical/rich-text'
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import {
    $exportReferenceMarkdown,
    $importReferenceMarkdown,
    ReferenceLexicalPlugin,
} from 'ComponentLibrary/src/molecules/TextEditor/DynamicReferences/ReferenceLexical'
import { ReferenceNode } from 'ComponentLibrary/src/molecules/TextEditor/DynamicReferences/ReferenceNode'
import { useReferenceChoices } from 'ComponentLibrary/src/molecules/TextEditor/DynamicReferences/referenceModel'

type FormTextFieldProps<TFieldValues extends FieldValues> = {
    label?: string
    control: Control<TFieldValues>
    fieldName: Path<TFieldValues>
    defaultValue: PathValue<TFieldValues, Path<TFieldValues>>
    errors: FieldErrors<TFieldValues>
    disabled?: boolean
    required?: boolean
    rules?:
        | Omit<RegisterOptions<TFieldValues, Path<TFieldValues>>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled' | 'required'>
        | undefined
    inputFieldStyles?: React.CSSProperties
    unit?: string
    hideLabel?: boolean
    placeholder?: string
    gridStyles?: React.CSSProperties
    enableHeadings?: boolean
    enableTextFormatting?: boolean
    enableBulletedList?: boolean
    enableNumberedList?: boolean
    preserveMarkdownNewLines?: boolean
    ariaLabel?: string
    autoFocus?: boolean
    copy?: Partial<FormMarkdownFieldCopy>
} & TextFieldWrapperProps

export type FormMarkdownFieldCopy = {
    toolbar: ToolbarPluginCopy
}

const LEXICAL_THEME = {
    text: {
        bold: 'lexical-editor-bold',
        italic: 'lexical-editor-italic',
    },
}

export function FormMarkdownField<TFieldValues extends FieldValues>(props: FormTextFieldProps<TFieldValues>) {
    const {
        label,
        control,
        fieldName,
        defaultValue,
        rules,
        errors,
        fullWidth,
        required,
        disabled,
        xs = 6,
        gridStyles,
        placeholder,
        hideLabel,
        inputFieldStyles,
        enableHeadings = false,
        enableTextFormatting = true,
        enableBulletedList = true,
        enableNumberedList = false,
        preserveMarkdownNewLines = false,
        ariaLabel,
        autoFocus = false,
        copy,
    } = props
    const referenceChoices = useReferenceChoices()

    // Get is used to handle subfields like foo.bar.baz
    const error = get(errors, fieldName)
    const errorMessage = error?.message?.toString()

    const createOnChangeHandler = (onChange: ControllerRenderProps<TFieldValues>['onChange']) => (editorState: EditorState) => {
        editorState.read(() => {
            const markdown = referenceChoices
                ? $exportReferenceMarkdown(preserveMarkdownNewLines)
                : $convertToMarkdownString(TRANSFORMERS, undefined, preserveMarkdownNewLines)
            onChange(markdown as PathValue<TFieldValues, Path<TFieldValues>>)
        })
    }

    return (
        <TextFieldWrapper fullWidth={fullWidth} xs={xs} label={label} required={required} hideLabel={hideLabel} styles={gridStyles}>
            <Controller
                control={control}
                defaultValue={defaultValue}
                name={fieldName}
                rules={{ ...rules, required: required ? { value: true, message: 'Pflichtfeld' } : undefined }}
                render={({ field }) => {
                    const initialValue = field.value ?? defaultValue

                    return (
                        <LexicalComposer
                            initialConfig={createInitialConfig(initialValue, preserveMarkdownNewLines, !!referenceChoices, !disabled)}
                        >
                            <EditorShell>
                                <ToolbarPlugin
                                    enableHeadings={enableHeadings}
                                    enableTextFormatting={enableTextFormatting}
                                    enableBulletedList={enableBulletedList}
                                    enableNumberedList={enableNumberedList}
                                    copy={copy?.toolbar}
                                />
                                <OnChangePlugin onChange={createOnChangeHandler(field.onChange)} />
                                <RichTextPlugin
                                    contentEditable={
                                        <TextInputContainer sx={inputFieldStyles}>
                                            <StyledContentEditable disabled={disabled} ariaLabel={ariaLabel ?? fieldName} />
                                            <FormHelperText
                                                variant="outlined"
                                                sx={{
                                                    fontSize: '0.5rem',
                                                    position: 'absolute',
                                                    bottom: '-14px',
                                                    whiteSpace: 'nowrap',
                                                    right: '-12px',
                                                }}
                                                margin="dense"
                                                error
                                            >
                                                {errorMessage || ' '}
                                            </FormHelperText>
                                        </TextInputContainer>
                                    }
                                    placeholder={<Placeholder>{placeholder}</Placeholder>}
                                    ErrorBoundary={LexicalErrorBoundary}
                                />
                                <HistoryPlugin />
                                <ListPlugin />
                                <EditablePlugin disabled={disabled} />
                                {referenceChoices && <ReferenceLexicalPlugin choices={referenceChoices} />}
                                {autoFocus && <AutoFocusPlugin />}
                            </EditorShell>
                        </LexicalComposer>
                    )
                }}
            />
        </TextFieldWrapper>
    )
}

const TextInputContainer = styled(Box)`
    width: 100%;
    grid-row: 2;
    grid-column: 1;
    padding: 12px;
    min-height: 80px;
    max-height: calc(50vh - 50px);
    overflow-y: auto;
    border: 1px solid #ccc;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    border-top: none;
    overflow-x: hidden;
`
const Placeholder = styled('div')`
    position: relative;
    max-height: 40px;
    font-size: 16px;
    margin: 12px 14px;
    grid-row: 2;
    grid-column: 1;
    color: ${tokens.ColorTextSubtle};
    overflow: clip;
    pointer-events: none;
`

const EditorShell = styled('div')`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
`

const StyledContentEditable = styled(ContentEditable)`
    font-size: 16px;
    min-height: 56px;
    grid-column: 1;
    :focus-visible {
        outline: none;
    }
    & > p {
        margin: 0;
    }
`
export default FormMarkdownField

function EditablePlugin({ disabled }: { disabled?: boolean }) {
    const [editor] = useLexicalComposerContext()

    React.useEffect(() => editor.setEditable(!disabled), [disabled, editor])
    return null
}

function createInitialConfig(
    initialValue: string | undefined,
    preserveMarkdownNewLines: boolean,
    enableReferences: boolean,
    editable: boolean
) {
    const editorState = initialValue
        ? () =>
              enableReferences
                  ? $importReferenceMarkdown(initialValue, preserveMarkdownNewLines)
                  : $convertFromMarkdownString(initialValue, TRANSFORMERS, undefined, preserveMarkdownNewLines)
        : undefined

    return {
        namespace: 'RichTextEditor',
        editable,
        theme: LEXICAL_THEME,
        nodes: [...(enableReferences ? [ReferenceNode] : []), ListNode, ListItemNode, HeadingNode],
        editorState,
        onError: (error: Error) => {
            throw error
        },
    }
}
